// Training Academy > Participants (added 19 Oct 2026).
// Every participant ever registered, searchable, with Edit and Delete.
// Backend: GET / PATCH / DELETE /api/trainings/participants in routes/trainings.js.
// Editing changes the participant record only: a certificate already issued keeps
// the name it was issued with (revoke and re-issue to correct it).
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CertStatus, fmtDate } from './TrainingCertificates';
import useSortable, { SortHeader } from '../hooks/useSortable';

const API = 'https://carriera-backend.onrender.com';
// Click-to-sort columns (19 Oct 2026). Programme sorts by course date; "Registered" =
// when the company signed this person up.
const SORTERS = {
  name:       { value: r => r.full_name },
  company:    { value: r => r.company_name },
  job:        { value: r => r.job_title },
  email:      { value: r => r.email },
  programme:  { value: r => r.training?.date, firstDir: 'desc' },
  attended:   { value: r => (r.attended ? 1 : 0), firstDir: 'desc' },
  registered: { value: r => r.created_at, firstDir: 'desc' },
};
const TH = 'px-3 py-2 font-normal whitespace-nowrap';

const FIELDS = [
  { key: 'full_name', label: 'Full name', required: true },
  { key: 'job_title', label: 'Job title' },
  { key: 'email',     label: 'Email', type: 'email' },
  { key: 'phone',     label: 'Phone' },
];

export default function TrainingParticipants() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [editing, setEditing] = useState(null);   // row being edited
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const inp = "w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent";
  const lbl = "block text-xs text-carriera-muted uppercase tracking-widest mb-1";

  useEffect(() => {
    axios.get(`${API}/api/trainings/participants`, { headers })
      .then(res => setRows(res.data))
      .catch(() => alert('Failed to load participants.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r => [r.full_name, r.company_name, r.email, r.phone, r.job_title, r.training?.title, r.certificate?.serial]
      .some(v => String(v || '').toLowerCase().includes(term)));
  }, [rows, q]);
  const { sorted, sort, toggle } = useSortable(shown, SORTERS, { key: 'registered', dir: 'desc' });

  function startEdit(r) {
    setEditing(r);
    setForm(Object.fromEntries(FIELDS.map(f => [f.key, r[f.key] || ''])));
  }

  async function saveEdit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/api/trainings/participants/${editing.id}`, form, { headers });
      setRows(prev => prev.map(r => r.id === editing.id ? { ...r, ...res.data } : r));
      setEditing(null);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  }

  async function remove(r) {
    const certNote = r.certificate ? `\n\nTheir certificate ${r.certificate.serial} stays valid (it was genuinely issued).` : '';
    if (!window.confirm(`Delete ${r.full_name} (${r.company_name || 'no company'}, ${r.training?.title || 'no programme'})?\n\nThis cannot be undone. The company's registration stays.${certNote}`)) return;
    try {
      await axios.delete(`${API}/api/trainings/participants/${r.id}`, { headers });
      setRows(prev => prev.filter(x => x.id !== r.id));
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  }

  const nameChanged = editing && form.full_name?.trim() !== (editing.full_name || '').trim();

  return (
    <div className="h-full flex flex-col">
      <div className="mb-6">
        <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Academy</p>
        <h2 className="font-serif text-3xl font-normal text-carriera-text">Training Participants</h2>
      </div>

      {loading ? <p className="text-sm text-carriera-muted">Loading participants...</p> : (
        <div className="flex-1 flex flex-col min-h-0">
          <input type="search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search name, company, email, phone, programme or certificate no."
            className={`${inp} mb-4`} />
          <p className="text-xs text-carriera-muted mb-2">{shown.length} of {rows.length} participants</p>
          <div className="bg-white border border-carriera-border flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-carriera-bg">
                <tr className="text-left text-xs text-carriera-muted uppercase tracking-widest">
                  <SortHeader label="Name" sortKey="name" sort={sort} onSort={toggle} className={TH} />
                  <SortHeader label="Company" sortKey="company" sort={sort} onSort={toggle} className={TH} />
                  <SortHeader label="Job Title" sortKey="job" sort={sort} onSort={toggle} className={TH} />
                  <SortHeader label="Email" sortKey="email" sort={sort} onSort={toggle} className={TH} />
                  <th className={TH}>Phone</th>
                  <SortHeader label="Programme" sortKey="programme" sort={sort} onSort={toggle} className={TH} />
                  <SortHeader label="Attended" sortKey="attended" sort={sort} onSort={toggle} className={TH} />
                  <th className={TH}>Certificate</th>
                  <SortHeader label="Registered" sortKey="registered" sort={sort} onSort={toggle} className={TH} />
                  <th className={TH}></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => (
                  <tr key={r.id} className="border-t border-carriera-border align-top">
                    <td className="px-3 py-2 font-medium text-carriera-text">{r.full_name}</td>
                    <td className="px-3 py-2 text-carriera-sec">{r.company_name || '-'}</td>
                    <td className="px-3 py-2 text-carriera-sec">{r.job_title || '-'}</td>
                    <td className="px-3 py-2 text-carriera-sec">{r.email || '-'}</td>
                    <td className="px-3 py-2 text-carriera-sec whitespace-nowrap">{r.phone || '-'}</td>
                    <td className="px-3 py-2 text-carriera-sec">
                      {r.training?.title || '-'}
                      <span className="block text-xs text-carriera-muted">{fmtDate(r.training?.date)}</span>
                    </td>
                    <td className="px-3 py-2 text-carriera-sec">{r.attended ? 'Yes' : '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><CertStatus cert={r.certificate} headers={headers} /></td>
                    <td className="px-3 py-2 text-carriera-sec whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <button onClick={() => startEdit(r)} className="text-xs text-carriera-accent hover:underline mr-3">Edit</button>
                      <button onClick={() => remove(r)} className="text-xs text-red-400 hover:text-red-600">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => !saving && setEditing(null)}>
          <form onSubmit={saveEdit} className="bg-white w-full max-w-md p-6 shadow-2xl space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="font-serif text-xl font-normal text-carriera-text">Edit participant</h3>
              <p className="text-xs text-carriera-muted mt-1">{editing.company_name || 'No company'} · {editing.training?.title || 'No programme'}</p>
            </div>
            {FIELDS.map(f => (
              <div key={f.key}>
                <label className={lbl}>{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                <input type={f.type || 'text'} required={f.required} value={form[f.key]}
                  onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className={inp} />
              </div>
            ))}
            {editing.certificate && nameChanged && (
              <p className="text-xs bg-yellow-50 border border-yellow-200 text-yellow-800 p-3">
                Certificate {editing.certificate.serial} was already issued with the old name and will not change.
                To correct it, the certificate must be revoked and issued again.
              </p>
            )}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" disabled={saving} onClick={() => setEditing(null)} className="text-sm text-carriera-muted hover:text-carriera-text">Cancel</button>
              <button type="submit" disabled={saving}
                className="bg-carriera-text text-white text-sm font-medium px-4 py-2 hover:bg-carriera-accent disabled:opacity-50">
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
